import type { UTM } from 'stores/zustand/attributionStore';
import { toTitleCase, upperFirstLetter } from 'utils/caseing';

export interface ClientEvent {
    event_type:
        | 'explore_engagement'
        | 'home_feed_market_click'
        | 'trading_page_visit'
        | 'watchlist_update'
        | 'attribution_answer'
        | 'push_notification_action'
        | 'signup_step'
        | 'signup_error'
        | 'deposit_step'
        | 'search_query'
        | 'search_session'
        | 'search_result'
        | 'related_series_click'
        | 'sports_engagement'
        | 'page_load'
        | 'impression'
        | 'click'
        | 'deposit_impression'
        | 'review_order'
        | 'submit_order'
        | 'signup_view'
        | 'signup_click'
        | 'install_click'
        | 'deposit_initiated'
        | 'social_engagement'
        | 'share'
        | 'live_chat_reply_long_press'
        | 'live_chat_reply_tap'
        | ClientEventLiveChatReactionEventType
        | ClientEventPerpsFundingEventType
        | ClientEventPerpsOnboardingEventType
        | ClientEventPerpsTradingEventType;
    event_properties:
        | ClientEventExploreEngagement
        | ClientEventHomeFeedMarketClick
        | ClientEventTradingPageVisit
        | ClientEventWatchlistUpdate
        | ClientEventAttibutionAnswer
        | ClientEventPushNotificationPermissionAction
        | SignUpStep
        | SignUpError
        | DepositStep
        | ClientEventSearchQuery
        | ClientEventSearchSession
        | ClientEventSearchResultClick
        | ClientEventRelatedSeriesClick
        | ClientEventSportsEngagement
        | ClientEventPageLoad
        | ClientEventImpression
        | ClientEventClick
        | ClientEventDepositImpression
        | ClientEventOrder
        | ClientEventSignupView
        | ClientEventSignupClick
        | ClientEventInstallClick
        | ClientEventDepositInitiated
        | ClientEventSocialEngagement
        | ClientEventShare
        | ClientEventLiveChatReply
        | ClientEventLiveChatReaction
        | ClientEventPerpsFunding
        | ClientEventPerpsOnboarding
        | ClientEventPerpsTrading;
    common_properties: ClientEventCommonProperties;
}

export type OSType = 'iOS' | 'Android' | 'Windows Phone' | 'Windows' | 'MacOS' | 'Linux' | 'unknown';

export interface ClientEventCommonProperties {
    platform: 'web' | 'app' | 'api';
    device?: 'desktop' | 'mobile' | 'tablet';
    operating_system?: OSType;
    country_code?: string;
    event_id?: string;
    timestamp?: string; // ISO 8601 format, UTC timezone
    client_timezone?: string; // IANA timezone name, e.g. "America/New_York"
    user_id?: string;
    session_id?: string;
    app_version?: string;
    referrer?: string; // URL of the previous page
    utm?: UTM;
}

export interface ClientEventExploreEngagement {
    action: 'expanded_price' | 'hovered';
    entrypoint: 'explore' | 'search' | 'other' | 'unknown';
    series_ticker: string;
}

export interface ClientEventTradingPageVisit {
    series_ticker: string;
    event_ticker?: string;
    market_ticker?: string;
    entrypoint:
        | 'direct'
        | 'search'
        | 'portfolio'
        | 'explore'
        | 'all_markets'
        | 'watchlist'
        | 'notification'
        | 'other'
        | 'unknown';
}

export interface ClientEventWatchlistUpdate {
    series_ticker: string;
    action: 'added' | 'removed';
    entrypoint: 'trading_page' | 'search' | 'explore' | 'portfolio' | 'watchlist' | 'unknown';
}

export interface ClientEventOrder {
    event_ticker?: string;
    series_ticker?: string;
    order_type?: string;
    order_action?: string;
    market_price?: number;
    order_price?: number;
    order_id?: string;
    traded_size?: number;
    resting_size?: number;
    value?: number;
    portfolio?: boolean;
    first_trade?: boolean;
    market_ticker?: string;
    event_category?: string;
    event_subcategories?: string[];
    side?: string;
    // Last-touch attribution captured on the event page (30-min TTL, event-scoped);
    // absent for organic orders.
    attribution_source?: 'social_post' | 'social_activity';
    attribution_post_id?: string;
    attribution_event_ticker?: string;
    attribution_age_ms?: number;
}

export interface ClientEventAttibutionAnswer {
    answer: string;
}

export interface ClientEventPushNotificationPermissionAction {
    action: 'requested' | 'accepted' | 'denied';
}

export type SignUpStepName =
    | 'skipSignUpNotNow'
    | 'signedUp'
    | 'emailVerified'
    | 'referralCode'
    | 'prefillBirthday'
    | 'prefillPhone'
    | 'prefillCode'
    | 'confirmInfo'
    | 'prefillEditName'
    | 'prefillEditBirthday'
    | 'prefillEditPhone'
    | 'prefillEditSsn'
    | 'prefillEditAddress'
    | 'profile'
    | 'profileAddress'
    | 'profileSsn'
    | 'incentives'
    | 'incentivesClaim'
    | 'incentivesAwarded'
    | 'selectDepositMethod'
    | 'depositToKalshi'
    | 'loadingBankAccount'
    | 'reviewDeposit'
    | 'depositLoading'
    | 'depositResult'
    | 'stepSurvey'
    | 'stepOnTheGo'
    | 'enterInfoManually'
    | 'verifyPrefillCodeEmpty'
    | 'verifyPhoneCodeInit'
    | 'verifyPhoneCodeSubmit'
    | 'verifyPhoneCodeSucess'
    | 'verifyPhoneCodeInvalid'
    | 'verifyPhoneCodeDuplicatedAccount'
    | 'resendPhoneCode'
    | 'digitalUnknown'
    | 'digitalAccountCreated'
    | 'digitalEmailVerified'
    | 'digitalKycStarted'
    | 'digitalKycDocumentsRequested'
    | 'digitalKycManualReviewRequested'
    | 'digitalKycApproved'
    | 'digitalKycRejected'
    | 'digitalDuplicatedEmail'
    | 'digitalMissingProfileInfo'
    | 'digitalVerificationFinish'
    | 'openSocure'
    | 'cameraPermissionGranted'
    | 'cameraPermissionDenied'
    | 'socureSuccess'
    | 'socureError'
    | 'socureCancel'
    | 'startDocv'
    | 'docvDobError'
    | 'docvNameError'
    | 'docvIdExpiredError'
    | 'docvImageIssueError'
    | 'docvError'
    | 'docvApproved'
    | 'docvResubmit'
    | 'docvRejected'
    | 'docvReviewNeeded'
    | 'accountCreated'
    // RiskOS Advanced Prefill (Socure) signup analytics
    | 'riskosPrefillShown'
    | 'riskosPrefillStarted'
    | 'riskosPrefillSsn4Submitted'
    | 'riskosPrefillOtpShown'
    | 'riskosPrefillOtpSubmitted'
    | 'riskosPrefillOtpResendRequested'
    | 'riskosPrefillDisplayed'
    | 'riskosPrefillEditedAfterPrefill'
    | 'riskosPrefillCompleted'
    | 'riskosPrefillFallback'
    | 'riskosPrefillError'
    | 'riskosPrefillCleared';

export interface SignUpStep {
    account_mode?: 'unknown' | 'standard' | 'perps_only';
    has_referral?: boolean;
    referral_code?: string;
    step: SignUpStepName;
    kyc_flow_id?: string;
    frontend_session_id?: string;
    surface?: string;
    entrypoint?: string;
    email_verification_method?: 'magic_link' | 'manual_code';
    kyc_step_group?: 'profile' | 'address' | 'phone' | 'dob' | 'ssn' | 'verification' | 'docv' | 'selfie' | 'unknown';
    field_group?:
        | 'name'
        | 'dob'
        | 'address'
        | 'phone'
        | 'ssn4'
        | 'ssn9'
        | 'national_id'
        | 'docv'
        | 'selfie'
        | 'unknown';
    field_started?: boolean;
    field_completed?: boolean;
    field_valid?: boolean;
    field_prefilled?: boolean;
    field_edited_after_prefill?: boolean;
    validation_code?: string;
    submit_result?: 'started' | 'succeeded' | 'failed' | 'unknown';
    provider?: 'socure' | 'sardine' | 'verified' | 'internal' | 'unknown';
    verification_result?: 'approved' | 'rejected' | 'manual_review' | 'documents_requested' | 'error' | 'unknown';
    docv_required?: boolean;
    docv_started?: boolean;
    docv_submitted?: boolean;
    docv_result?: 'approved' | 'rejected' | 'resubmit' | 'review' | 'error' | 'unknown';
    selfie_required?: boolean;
    selfie_started?: boolean;
    selfie_submitted?: boolean;
    selfie_result?: 'approved' | 'rejected' | 'resubmit' | 'error' | 'unknown';
    error_code?: string;
    error_source?: 'validation' | 'provider' | 'backend' | 'risk' | 'unknown';
    retryable?: boolean;
    rum_session_id?: string;
    rum_view_id?: string;
}

export interface SignUpError {
    error: string;
}

export type DepositStepName =
    | 'openSelectDepositMethod'
    | 'bankSelected'
    | 'cryptoSelected'
    | 'newCardSelected'
    | 'inAppPaySelected'
    | 'wireTransferSelected'
    | 'openReviewDeposit'
    | 'submitBankDeposit'
    | 'submitCardDeposit'
    | 'submitBankDepositInit'
    | 'submitBankDepositError'
    | 'submitBankDepositSuccess'
    | 'openInAppPay'
    | 'inAppPayCanceled'
    | 'inAppPayError'
    | 'inAppPayResult'
    | 'inAppPayCreateTokenInit'
    | 'inAppPayCreateTokenResult'
    | 'inAppPayCreateTokenSuccess'
    | 'inAppPayCreateTokenError'
    | 'openNewCard'
    | 'newCardCreateTokenInit'
    | 'newCardCreateTokenError'
    | 'newCardCreateTokenCancel'
    | 'newCardCreateTokenSuccess'
    | 'submitCardInstrumentInit'
    | 'submitCardInstrumentError'
    | 'submitCardInstrumentSuccess'
    | 'submitCardTokenInit'
    | 'submitCardTokenSuccess'
    | 'cardChallengeRequested'
    | 'submitCardTokenError'
    | 'cardChallengeRequestedInit'
    | 'cardChallengeRequestedSuccess'
    | 'cardChallengeRequestedError'
    | 'cardDepositSuccess'
    | 'cryptoNyBlocked'
    | 'cryptoOpenCitizenShip'
    | 'closeCryptoDeposit'
    | 'depositFlowOpened'
    | 'methodSelectionViewed'
    | 'methodSelectionClosed'
    | 'methodSelected'
    | 'kycBlocked'
    | 'providerOpenStarted'
    | 'providerOpenSucceeded'
    | 'providerOpenFailed'
    | 'venmoConnectClicked'
    | 'amountScreenViewed'
    | 'amountEntered'
    | 'suggestedAmountSelected'
    | 'depositLimitViewed'
    | 'reviewClicked'
    | 'walletButtonClicked'
    | 'submitClicked'
    | 'submitStarted'
    | 'submitFailed'
    | 'submitSucceeded'
    | 'resultViewed'
    | 'depositFlowDismissed'
    | 'partialFundingViewed'
    | 'partialFundingContinue'
    | 'partialFundingChangeMethod';

export interface DepositStep {
    step: DepositStepName;
    deposit_flow_id?: string;
    funnel_name?: 'deposit' | 'first_deposit' | string;
    funnel_version?: string;
    surface?: string;
    entrypoint?: string;
    payment_method?:
        | 'bank'
        | 'card'
        | 'apple_pay'
        | 'google_pay'
        | 'paypal'
        | 'venmo'
        | 'cashapp'
        | 'crypto'
        | 'wire'
        | 'unknown';
    rail?: 'ach' | 'debit' | 'apm' | 'crypto' | 'wire' | 'unknown';
    provider?:
        | 'plaid'
        | 'aeropay'
        | 'stripe'
        | 'checkout'
        | 'braintree'
        | 'zerohash'
        | 'mesh'
        | 'manual_wire'
        | 'internal'
        | 'unknown';
    page_load_id?: string;
    frontend_session_id?: string;
    amount_cents?: number;
    amount_source?: 'typed' | 'suggestion' | 'default' | 'saved_amount' | 'unknown';
    saved_payment_method?: boolean;
    is_signup?: boolean;
    is_first_deposit_context?: boolean;
    account_mode?: 'standard' | 'perps_only' | 'unknown';
    resolved_destination?: 'event_contract' | 'margin' | 'unknown';
    limit_type?:
        | 'daily_deposit_limit'
        | 'method_deposit_limit'
        | 'account_deposit_limit'
        | 'docv_without_verification_limit'
        | 'in_flight_deposit_limit'
        | 'monthly_deposit_limit'
        | 'unknown';
    limit_scope?:
        | 'account'
        | 'ach'
        | 'bank_account'
        | 'debit'
        | 'paypal'
        | 'venmo'
        | 'cashapp'
        | 'crypto'
        | 'wire'
        | 'unknown';
    deposit_limit_cents?: number;
    deposit_limit_remaining_cents?: number;
    deposit_limit_reason?: string;
    limit_exceeded?: boolean;
    error_code?: string;
    error_source?: 'validation' | 'provider' | 'backend' | 'risk' | 'unknown';
    block_reason?: 'document_verification_required' | 'applicant_signup_required' | 'unknown';
    provider_error_code?: string;
    retryable?: boolean;
    deposit_id?: string;
    transfer_id?: string;
    payment_intent_id?: string;
    last_step?: string;
    dismissal_reason?: 'deposit_later' | 'close' | 'background' | 'programmatic' | 'unknown';
    dismissal_source?: 'deposit_later_cta' | 'modal_close' | 'modal_backdrop' | 'programmatic' | 'unknown';
    hidden_duration_ms?: number;
    rum_session_id?: string;
    rum_view_id?: string;
}

export interface ClientEventHomeFeedMarketClick {
    feed_index: number;
    entrypoint: string;
    series_ticker: string;
    event_ticker: string;
    click_type?: 'inline_trade';
    trade_details?: {
        side: 'yes' | 'no';
        team?: 'home' | 'away' | 'tie';
    };
}

export interface ClientEventSearchQuery {
    query: string;
    search_type: 'navbar' | 'page' | 'for_you';
    filters?: {
        category?: string;
        status?: string;
        order_by?: string;
        tag?: string;
    };
    result_count: number;
    session_id?: string;
}

export interface ClientEventSearchSession {
    session_id: string;
    action: 'start' | 'end' | 'abandon';
    duration_ms?: number; // Time from start to end/abandon
    total_queries?: number;
    total_clicks?: number;
    click_positions?: number[]; // Positions of clicked results
    time_to_first_click_ms?: number;
}

export interface ClientEventSearchResultClickBase {
    query: string;
    search_type: 'navbar_dropdown' | 'search_results_page';
    search_index: number;
    session_id?: string;
    time_since_search_ms?: number;
    search_section?: 'markets' | 'perps' | 'props' | 'profiles' | 'suggested_pages' | 'recent_searches';
}

export type ClientEventSearchResultIdentity =
    | {
          result_type: 'event';
          series_ticker: string;
          event_ticker: string;
      }
    | {
          result_type: 'perp';
          series_ticker: string;
          event_ticker: string;
      }
    | {
          result_type: 'profile';
          profile_nickname: string;
      }
    | {
          result_type: 'category' | 'subcategory' | 'competition' | 'milestone' | 'topic';
          result_label: string;
          destination_path: string;
      }
    | {
          result_type: 'search_term';
          selected_query: string;
      };

export type ClientEventSearchResultClick = ClientEventSearchResultClickBase & ClientEventSearchResultIdentity;

export interface ClientEventRelatedSeriesClick {
    series_ticker: string;
    event_ticker?: string;
    source_series_ticker: string;
    source_event_ticker?: string;
    index: number;
}

export interface ClientEventSportsEngagement {
    action:
        | 'updateSportsLayout'
        | 'selectSpreadHomeTeam'
        | 'selectSpreadAwayTeam'
        | 'openSpreadPopover'
        | 'selectSpreadMarket'
        | 'openTotalPopover'
        | 'selectTotalMarket'
        | 'openPropsModal'
        | 'selectProp'
        | 'selectPropTab'
        | 'openPropsMarketSearch'
        | 'clickSeeMoreProps'
        | 'toggleLadder'
        | 'openLadderExplainer'
        | 'openLadderPayouts'
        | 'toggleEscalator'
        | 'openEscalatorExplainer'
        | 'openEscalatorPayouts';
}

export type PageType =
    | 'explore'
    | 'browse'
    | 'category'
    | 'live'
    | 'sports'
    | 'event'
    | 'portfolio'
    | 'watchlist'
    | 'search'
    | 'ideas'
    | 'account'
    | 'signup_step'
    | 'deposit'
    | 'shareable_modal'
    | 'perps_credit_enrollment'
    | 'perps_credit_terms'
    | 'hub'
    | 'other';

export type ContainerType =
    | 'page'
    | 'modal'
    | 'dropdown'
    | 'section'
    | 'list'
    | 'grid'
    | 'carousel'
    | 'tab'
    | 'slideshow';
export type ItemType = 'series' | 'event' | 'market' | 'post' | 'category' | 'button' | 'other';

// Search filter context attached to page_type='search' page_load events.
export interface SearchFilterMetadata {
    query?: string;
    status?: string;
    order_by?: string;
    category?: string;
    prop_only?: boolean;
    exclude_sports?: boolean;
    frequency?: string;
    reverse?: boolean;
}

export interface ClientEventPageLoad {
    page_load_id: string;
    page_type: PageType;
    page_name: string; // Human readable name of the page, e.g. "Explore".
    page_id?: string; // Unique identifier for the page.
    ticker?: string;
    sort?: string;
    filter?: SearchFilterMetadata;
    format?: string;
    pathname?: string; // Current page URL path
    structured_target?: string;
    structured_target_id?: string;
    league?: string;
    // Market-specific (populated when page_type is "event")
    series_ticker?: string;
    event_ticker?: string;
    entrypoint?: string;
    event_category?: string;
    event_subcategories?: string[];
    orderbook_view?: 'maker' | 'taker' | 'makerAndTaker' | 'oneSided';
    orderbook_show_full_orderbook?: boolean;
    orderbook_show_sell_prices?: boolean;
    orderbook_header_expanded?: boolean;
    orderbook_collapse_to_cents?: boolean;
    orderbook_hide_fractional_shares?: boolean;
    orderbook_show_incentive_details?: boolean;
    market_ticker?: string; // Stat ladder payout matrix: the ladder market it opened from.
    prop?: string;
    /** Scalar formats: which format an event is about, `ladder` | `escalator`. */
    scalar_format?: string;
    /** Scalar formats: every format the chip row offered, e.g. "ladder,escalator". */
    formats_available?: string;
}

export interface ClientEventImpression {
    impression_id: string;
    page_load_id: string;
    container_type?: ContainerType;
    container_id?: string; // Unique identifier for the container.
    item_type?: ItemType;
    item_id?: string; // Unique identifier for the item.
    parent_type?: ItemType;
    parent_id?: string; // Unique identifier for the parent item, if applicable.
    position?: number; // Position of the item within the container, starting at 0.
    name?: string; // Human readable name of the item, e.g. "Will Team A win?".
    value?: string; // Additional value associated with the item, e.g. "Yes" or "No".
    format?: string; // Format of the item, e.g. "card", "row", "compact".
    // Social post context (formats 'ideas_post' / 'activity_item'). item_id stays
    // event_ticker for dashboard continuity; per-post identity rides here.
    post_id?: string;
    post_type?: string;
    author_nickname?: string;
    has_trade_slip?: boolean;
    trade_slip_status?: ClientEventSocialTradeSlipStatus;
    generation_id?: string;
    card_index?: number;
    seen_from?: string;
    prop?: string; // Stat ladders: the prop the ladder counts, e.g. "Passing Yards".
    /** Scalar formats: which format an event is about, `ladder` | `escalator`. */
    scalar_format?: string;
    /** Scalar formats: every format the chip row offered, e.g. "ladder,escalator". */
    formats_available?: string;
}

export interface ClientEventClick {
    click_id: string;
    impression_id?: string; // ID of the associated impression, if applicable.
    page_load_id: string;
    page_id?: string;
    pathname?: string;
    container_type?: ContainerType;
    container_id?: string; // Unique identifier for the container.
    item_type?: ItemType;
    item_id?: string; // Unique identifier for the item.
    parent_type?: ItemType;
    parent_id?: string; // Unique identifier for the parent item, if applicable.
    position?: number; // Position of the item within the container, starting at 0.
    name?: string; // Human readable name of the item, e.g. "Will Team A win?".
    value?: string; // Additional value associated with the item, e.g. "Yes" or "No".
    format?: string; // Format of the item, e.g. "card", "row", "compact".
    click_location?: string;
    structured_target?: string;
    structured_target_id?: string;
    league?: string;
    tab_name?: string;
    status?: 'enabled' | 'disabled';
    destination_event_ticker?: string;
    series_ticker?: string;
    event_ticker?: string;
    market_ticker?: string;
    event_category?: string;
    event_subcategories?: string[];
    buy_or_sell?: string;
    side?: string;
    // Social post context — mirrors ClientEventImpression; spread through from
    // the post impression props by useImpressionTracker.handleClickMetrics.
    post_id?: string;
    post_type?: string;
    author_nickname?: string;
    has_trade_slip?: boolean;
    trade_slip_status?: ClientEventSocialTradeSlipStatus;
    generation_id?: string;
    card_index?: number;
    seen_from?: string;
    prop?: string;
    /** Scalar formats: which format an event is about, `ladder` | `escalator`. */
    scalar_format?: string;
    /** Scalar formats: every format the chip row offered, e.g. "ladder,escalator". */
    formats_available?: string;
}

export interface ClientEventShare {
    shareable_type: 'marketLink' | 'perpsMarketLink';
    event_ticker?: string;
    market_ticker?: string;
}

export interface ClientEventLiveChatReply {
    event_ticker: string;
    market_ticker?: string;
    is_perp: boolean;
    message_id: string;
}

export type ClientEventLiveChatReactionEventType =
    | 'live_chat_reaction_picker_open'
    | 'live_chat_reaction_add_attempt'
    | 'live_chat_reaction_add_success'
    | 'live_chat_reaction_add_failure'
    | 'live_chat_reaction_remove_attempt'
    | 'live_chat_reaction_remove_success'
    | 'live_chat_reaction_remove_failure'
    | 'live_chat_reaction_asset_select'
    | 'live_chat_reaction_details_open';

export interface ClientEventLiveChatReaction {
    event_ticker: string;
    interaction_source: string;
    is_perp: boolean;
    market_ticker?: string;
    message_id: string;
    reaction_kind?: 'asset' | 'emoji';
    reaction_type?: string;
    room: 'event' | 'perps';
}

export interface ClientEventDepositImpression {
    impression_id: string;
    arm_id: number; // -1 = pre-bandit baseline (hardcoded suggestions)
    payment_method: NonNullable<DepositStep['payment_method']>; // granular method; payment_rail derived in dbt
    suggestions: string; // comma-separated, e.g. "250,1000,5000"
    page_load_id: string;
    selection_mode: 'baseline' | 'explore' | 'exploit' | 'segment_default' | 'fallback'; // how the arm was chosen
    epsilon: number; // exploration rate at assignment time (0 for baseline)
}

export interface ClientEventSignupView {
    has_referral?: boolean;
}

export interface ClientEventSignupClick {
    method: 'google' | 'apple' | 'email';
}

export interface ClientEventInstallClick {
    source: string;
    store: 'app_store' | 'play_store';
    auto?: boolean;
}

export interface ClientEventDepositInitiated {
    amount: number;
    currency: string;
    deposit_method: string;
    first_deposit?: boolean;
    high_value?: boolean;
}

export type ClientEventSocialEngagementStep =
    | 'composer_opened'
    | 'composer_position_attached'
    | 'composer_gif_attached'
    | 'composer_attachment_removed'
    | 'composer_submit_attempted'
    | 'composer_validation_blocked'
    | 'post_created'
    | 'post_create_failed';

export type ClientEventSocialComposerSource =
    | 'feed_inline'
    | 'market_page'
    | 'menu'
    | 'fab'
    | 'portfolio_share'
    | 'perps_market'
    | 'unknown';

export type ClientEventSocialTradeSlipStatus = 'open' | 'settled' | 'no_position';

export interface ClientEventSocialEngagement {
    surface: 'social';
    step: ClientEventSocialEngagementStep;
    // Joins all steps of one composer session (uuid).
    composer_flow_id?: string;
    composer_source?: ClientEventSocialComposerSource;
    page_load_id?: string;
    // Set on post_created.
    post_id?: string;
    post_type?: 'idea' | 'roi';
    scope_event_ticker?: string;
    event_ticker?: string;
    market_ticker?: string;
    side?: 'yes' | 'no';
    attachment_type?: 'position' | 'gif';
    has_position_attachment?: boolean;
    has_gif_attachment?: boolean;
    content_length?: number;
    validation_reason?: 'missing-position' | 'empty' | 'too-long';
    error_code?: string;
}

export interface ClientEventCarouselSwipe {
    container_type: 'perps_top_trades_carousel';
    container_id: string;
    to_index: number;
}

export type ClientEventPerpsMarketSocialClickAction = 'follow' | 'unfollow' | 'profile' | 'open_post' | 'badge';

export interface ClientEventPerpsMarketSocialClick {
    surface: 'perps_market';
    market_ticker: string;
    section: 'biggest_trade' | 'largest_positions' | 'market_feed';
    action: ClientEventPerpsMarketSocialClickAction;
}

// Stable FAQ analytics ids. Existing values preserve the original cross-platform event taxonomy.
export type ClientEventPerpsMarketFaqQuestion =
    | 'marketEnd'
    | 'leverage'
    | 'liquidation'
    | 'fundingRate'
    | 'difference'
    | 'regulated';

export interface ClientEventPerpsMarketFaqClick {
    surface: 'perps_market';
    market_ticker: string;
    question: ClientEventPerpsMarketFaqQuestion;
}

export interface ClientEventPerpsMarketFaqLearnMoreClick {
    surface: 'perps_market';
    market_ticker: string;
}

export interface ClientEventPerpsPredictionDataClick {
    surface: 'perps_market';
    market_ticker: string;
    timeframe: 'odds' | 'today' | 'week' | 'month' | 'year';
    event_ticker: string;
}

export type ClientEventPerpsTradingEventType =
    | 'perps_trade_panel_opened'
    | 'perps_trade_action_selected'
    | 'perps_trade_side_selected'
    | 'perps_trade_order_type_selected'
    | 'perps_trade_price_entered'
    | 'perps_trade_size_entered'
    | 'perps_trade_leverage_changed'
    | 'perps_trade_time_in_force_selected'
    | 'perps_trade_post_only_toggled'
    | 'perps_trade_tpsl_toggled'
    | 'perps_trade_tpsl_leg_edited'
    | 'perps_order_submit_clicked'
    | 'perps_order_enrollment_accepted'
    | 'perps_order_enrollment_reviewed'
    | 'perps_order_enrollment_dismissed'
    | 'perps_order_enrollment_result'
    | 'perps_order_credit_opt_out'
    | 'perps_order_submit_blocked'
    | 'perps_order_add_funds_requested'
    | 'perps_order_funding_route_selected'
    | 'perps_order_transfer_and_open_result'
    | 'perps_post_trade_tpsl_eligible'
    | 'perps_post_trade_tpsl_prompt_viewed'
    | 'perps_post_trade_tpsl_action_clicked'
    | 'perps_post_trade_tpsl_save_result'
    | 'perps_isolated_order_conflict_viewed'
    | 'perps_isolated_order_conflict_action_clicked'
    | 'perps_open_order_details_opened'
    | 'perps_open_order_cancel_clicked'
    | 'perps_open_order_edit_opened'
    | 'perps_open_order_tpsl_modal_opened'
    | 'perps_position_modify_clicked'
    | 'perps_position_close_clicked'
    | 'perps_position_order_modal_opened'
    | 'perps_position_tpsl_modal_opened';

export type ClientEventPerpsTradingEntrypoint =
    | 'market_page'
    | 'biggest_trade'
    | 'position_card'
    | 'position_details_drawer'
    | 'open_orders'
    | 'orderbook_click'
    | 'chart_click'
    | 'market_tile'
    | 'unknown';

export type ClientEventPerpsTradingPanelSurface = 'desktop' | 'mobile' | 'sidebar' | 'modal' | 'drawer';
export type ClientEventPerpsTradingMarginMode = 'cross' | 'isolated';
export type ClientEventPerpsTradingOrderAction = 'open' | 'close';
export type ClientEventPerpsTradingBookSide = 'bid' | 'ask';
export type ClientEventPerpsTradingPositionSide = 'long' | 'short' | 'flat';
export type ClientEventPerpsTradingOrderType = 'market' | 'limit';
export type ClientEventPerpsTradingTimeInForce = 'good_till_canceled' | 'immediate_or_cancel' | 'fill_or_kill';

export interface ClientEventPerpsTrading {
    /** Mobile-compatible ticker on shared margin-credit events. */
    ticker?: string;
    surface: 'perps_trading';
    entrypoint: ClientEventPerpsTradingEntrypoint;
    perps_trade_flow_id: string;
    market_ticker: string;
    margin_mode: ClientEventPerpsTradingMarginMode;
    order_action: ClientEventPerpsTradingOrderAction;
    side: ClientEventPerpsTradingBookSide;
    frontend_session_id?: string;
    panel_surface?: ClientEventPerpsTradingPanelSurface;
    position_side?: ClientEventPerpsTradingPositionSide;
    order_type?: ClientEventPerpsTradingOrderType;
    time_in_force?: ClientEventPerpsTradingTimeInForce;
    subaccount_number?: number;
    order_id?: string;
    rum_session_id?: string;
    rum_view_id?: string;
    initial_order_action?: ClientEventPerpsTradingOrderAction;
    initial_order_type?: ClientEventPerpsTradingOrderType;
    has_existing_position?: boolean;
    has_resting_order?: boolean;
    from_order_action?: ClientEventPerpsTradingOrderAction;
    to_order_action?: ClientEventPerpsTradingOrderAction;
    from_side?: ClientEventPerpsTradingBookSide;
    to_side?: ClientEventPerpsTradingBookSide;
    to_position_side?: Exclude<ClientEventPerpsTradingPositionSide, 'flat'>;
    from_order_type?: ClientEventPerpsTradingOrderType;
    to_order_type?: ClientEventPerpsTradingOrderType;
    limit_price_contract_dollars?: number;
    price_input_source?: 'manual' | 'price_offset';
    size_input_mode?: 'usd' | 'contracts';
    amount_cents?: number;
    contract_count?: number;
    leverage?: number;
    max_leverage?: number;
    leverage_percent?: number;
    post_only?: boolean;
    enabled?: boolean;
    leg?: 'take_profit' | 'stop_loss';
    input_type?: 'price' | 'percent';
    trigger_price_contract_dollars?: number;
    trigger_percent?: number;
    notional_cents?: number;
    margin_required_cents?: number;
    reduce_only?: boolean;
    tpsl_enabled?: boolean;
    blocked_reason?:
        | 'validation_error'
        | 'insufficient_funds'
        | 'stale_book'
        | 'notional_risk_limit'
        | 'opposite_position_side'
        | 'no_liquidity'
        | 'market_price_loading'
        | 'read_only_subuser'
        | 'close_without_position'
        | 'unknown';
    validation_field?: 'count' | 'price' | 'timeInForce' | 'postOnly' | 'reduceOnly' | 'takeProfit' | 'stopLoss';
    validation_error_type?:
        | 'invalid_count'
        | 'invalid_price'
        | 'price_precision'
        | 'post_only_tif_mismatch'
        | 'post_only_would_cross'
        | 'reduce_only_no_position'
        | 'reduce_only_wrong_side'
        | 'reduce_only_exceeds_position'
        | 'would_execute_as_taker'
        | 'no_market_price'
        | 'count_exceeds_max'
        | 'below_minimum_cost'
        | 'invalid_exit_trigger';
    available_balance_cents?: number;
    request_source?:
        | 'size_input_error'
        | 'submit_validation'
        | 'cross_no_available_funds'
        | 'modify_position_gate'
        | 'order_rejected_balance_too_low';
    required_amount_cents?: number;
    funding_route?: 'transfer_and_open' | 'transfer_only' | 'deposit' | 'retry';
    transfer_and_open_result?: 'completed' | 'transfer_failed' | 'timeout' | 'order_failed';
    conflict_source?: 'order_submit' | 'position_modify' | 'position_close';
    conflict_order_id?: string;
    conflict_side?: ClientEventPerpsTradingBookSide;
    conflict_action?: 'cancel_order' | 'go_back';
    open_source?: 'row_expand' | 'mobile_drawer';
    cancel_source?: 'open_orders_row' | 'open_order_drawer' | 'edit_order_modal' | 'conflict_modal';
    unfilled_percent?: number;
    edit_source?: 'open_orders_row' | 'open_order_drawer';
    has_existing_take_profit?: boolean;
    has_existing_stop_loss?: boolean;
    position_action?: ClientEventPerpsTradingOrderAction;
    position_contract_count?: number;
    position_equity_cents?: number;
    prompt_reason?: 'standard' | 'update_failed';
    exit_trigger_outcome?: 'not_requested' | 'applied' | 'failed';
    prompt_action?: 'set' | 'skip';
    action_stage?: 'prompt' | 'editor';
    save_result?: 'success' | 'failure';
    via?: 'scroll' | 'button';
    result?: 'enrolled' | 'conflict' | 'unknown';
}

export type ClientEventPerpsFundingEventType =
    | 'perps_funding_modal_opened'
    | 'perps_funding_source_viewed'
    | 'perps_funding_source_selected'
    | 'perps_deposit_handoff_started'
    | 'perps_deposit_handoff_result'
    | 'perps_transfer_step_viewed'
    | 'perps_transfer_direction_swapped'
    | 'perps_transfer_amount_entered'
    | 'perps_transfer_completed'
    | 'perps_transfer_failed'
    | 'perps_add_isolated_margin_modal_opened'
    | 'perps_add_isolated_margin_amount_entered'
    | 'perps_modify_position_add_funds_gate_viewed'
    | 'perps_modify_position_add_funds_gate_clicked'
    | 'perps_funding_modal_dismissed';

export type ClientEventPerpsFundingEntrypoint =
    | 'perps_account_balance'
    | 'position_card'
    | 'position_details_drawer'
    | 'modify_position_gate'
    | 'order_panel_add_funds'
    | 'transfer_dialog'
    | 'unknown';

export type ClientEventPerpsFundingContext = 'perps_account' | 'isolated_position' | 'modify_position_gate';
export type ClientEventPerpsFundingModalStep = 'idle' | 'add' | 'source' | 'transfer';
export type ClientEventPerpsFundingTransferDirection = 'predictions_to_perps' | 'perps_to_predictions';
export type ClientEventPerpsFundingTransferAccount = 'event_contract' | 'margined';

export interface ClientEventPerpsFunding {
    surface: 'perps_funding';
    entrypoint: ClientEventPerpsFundingEntrypoint;
    perps_funding_flow_id: string;
    funding_context: ClientEventPerpsFundingContext;
    frontend_session_id?: string;
    modal_step?: ClientEventPerpsFundingModalStep;
    margin_mode?: 'cross' | 'isolated';
    market_ticker?: string;
    subaccount_number?: number;
    position_side?: 'long' | 'short';
    rum_session_id?: string;
    rum_view_id?: string;
    available_balance_cents?: number;
    predictions_balance_cents?: number;
    transfer_available?: boolean;
    transfer_unavailable_reason?:
        | 'checking_exchange_status'
        | 'transfer_availability_unavailable'
        | 'exchange_inactive'
        | 'margin_exchange_inactive'
        | 'no_predictions_balance'
        | 'balance_unavailable'
        | 'unknown';
    funding_source?: 'deposit' | 'transfer';
    destination_account?: ClientEventPerpsFundingTransferAccount;
    deposit_flow_id?: string;
    deposit_opened?: boolean;
    handoff_result?: 'back_to_funding_source' | 'completed' | 'dismissed';
    deposit_result?: 'submit_succeeded' | 'submit_failed' | 'dismissed' | 'unknown';
    transfer_direction?: ClientEventPerpsFundingTransferDirection;
    source_account?: ClientEventPerpsFundingTransferAccount;
    source_balance_cents?: number;
    destination_balance_cents?: number;
    from_direction?: ClientEventPerpsFundingTransferDirection;
    to_direction?: ClientEventPerpsFundingTransferDirection;
    amount_cents?: number;
    amount_source?: 'typed' | 'percent_preset' | 'preset' | 'unknown';
    risk_level?: 'low' | 'medium' | 'high' | 'unknown';
    current_position_value_cents?: number;
    gate_reason?: 'position_value_below_required_cost' | 'unknown';
    dismissal_source?: 'modal_close' | 'modal_backdrop' | 'back_button' | 'programmatic' | 'unknown';
    last_step?: Exclude<ClientEventPerpsFundingModalStep, 'idle'>;
    error_message?: string;
}

export type ClientEventPerpsOnboardingEventType =
    | 'perps_waitlist_apply_clicked'
    | 'perps_onboarding_opened'
    | 'perps_intro_started'
    | 'perps_education_step_viewed'
    | 'perps_education_completed'
    | 'perps_application_step_viewed'
    | 'perps_application_terminal_viewed'
    | 'perps_application_done_clicked'
    | 'perps_application_explore_crypto_clicked'
    | 'perps_financial_profile_page_viewed'
    | 'perps_financial_profile_question_answered'
    | 'perps_financial_profile_validation_failed'
    | 'perps_financial_profile_ssn_field_shown'
    | 'perps_financial_profile_submitted'
    | 'perps_financial_profile_submit_failed'
    | 'perps_financial_profile_abandoned';

export type ClientEventPerpsOnboardingEntrypoint =
    | 'waitlist_apply_card'
    | 'waitlist_start_trading'
    | 'intent_apply_deeplink'
    | 'perps_access_gate'
    | 'perps_upsell_banner'
    | 'trade_page'
    | 'unknown';

export type ClientEventPerpsOnboardingApplicationFlow =
    | 'retail'
    | 'institutional_fcm'
    | 'institutional_scm'
    | 'unknown';

export type ClientEventPerpsOnboardingViewerState =
    | 'anon_unjoined'
    | 'anon_joined'
    | 'logged_in_unjoined'
    | 'logged_in_joined_locked'
    | 'logged_in_joined_unlocked'
    | 'unlocked_applicant';

export type ClientEventPerpsOnboardingModalView =
    | 'intro'
    | 'education'
    | 'national_id'
    | 'financial_profile'
    | 'clearing_mode'
    | 'legal_entity_identifier'
    | 'business_profile'
    | 'beneficial_ownership'
    | 'revenue_or_aum_band'
    | 'ecp_qualification'
    | 'regulatory_registration'
    | 'financial_documents'
    | 'agreements'
    | 'manual_review'
    | 'rejected'
    | 'approved_waitlisted'
    | 'provisioning'
    | 'success'
    | 'unknown';

export type ClientEventPerpsOnboardingBackendStatus =
    | 'not_started'
    | 'in_progress'
    | 'approved_for_agreements'
    | 'manual_review'
    | 'rejected'
    | 'approved_waitlisted'
    | 'provisioning'
    | 'complete';

export type ClientEventPerpsOnboardingApplicationStep =
    | 'financial_profile'
    | 'clearing_mode'
    | 'legal_entity_identifier'
    | 'business_profile'
    | 'beneficial_ownership'
    | 'revenue_or_aum_band'
    | 'ecp_qualification'
    | 'regulatory_registration'
    | 'financial_documents'
    | 'agreements';

export type ClientEventPerpsOnboardingCurrentStep = ClientEventPerpsOnboardingApplicationStep | 'empty';

export type ClientEventPerpsOnboardingTerminalState =
    | 'manual_review'
    | 'rejected'
    | 'approved_waitlisted'
    | 'provisioning'
    | 'success';

export type ClientEventPerpsFinancialProfileQuestion =
    | 'ssn'
    | 'marital_status'
    | 'number_of_dependents'
    | 'occupation'
    | 'occupation_description'
    | 'employer_name'
    | 'annual_income'
    | 'liquid_net_worth'
    | 'total_net_worth'
    | 'trading_experience'
    | 'reason_for_trading';

export type ClientEventPerpsFinancialProfilePage = 1 | 2;

export type ClientEventPerpsSsnRevealTrigger = 'initial' | 'after_submit_failed';

export interface ClientEventPerpsOnboarding {
    surface: 'perps_onboarding';
    entrypoint: ClientEventPerpsOnboardingEntrypoint;
    perps_onboarding_flow_id: string;
    application_flow: ClientEventPerpsOnboardingApplicationFlow;
    is_institutional: boolean;
    frontend_session_id?: string;
    viewer_state?: ClientEventPerpsOnboardingViewerState;
    modal_view?: ClientEventPerpsOnboardingModalView;
    backend_status?: ClientEventPerpsOnboardingBackendStatus;
    current_step?: ClientEventPerpsOnboardingCurrentStep;
    step_number?: number;
    total_steps?: number;
    rum_session_id?: string;
    rum_view_id?: string;
    logged_in?: boolean;
    is_applicant?: boolean;
    can_start_onboarding?: boolean;
    open_source?: 'manual_click' | 'deeplink' | 'auto_redirect';
    intro_version?: string;
    education_step_id?: 'leverage' | 'liquidation' | 'tpsl';
    education_step_index?: 0 | 1 | 2;
    education_step_count?: number;
    application_step?: ClientEventPerpsOnboardingApplicationStep;
    terminal_state?: ClientEventPerpsOnboardingTerminalState;
    rejection_reason?: string;
    application_page?: ClientEventPerpsFinancialProfilePage;
    financial_profile_question?: ClientEventPerpsFinancialProfileQuestion;
    invalid_questions?: ClientEventPerpsFinancialProfileQuestion[];
    answered_questions?: ClientEventPerpsFinancialProfileQuestion[];
    // Redundant with answered_questions.length, but a scalar is far easier to chart/segment in Amplitude.
    answered_question_count?: number;
    ssn_field_shown?: boolean;
    ssn_provided?: boolean;
    ssn_reveal_trigger?: ClientEventPerpsSsnRevealTrigger;
    error_code?: string;
}

export function resolvePageType(pathname: string): PageType {
    if (pathname === '/') {
        return 'explore';
    }
    if (pathname.startsWith('/search')) {
        return 'search';
    }
    if (pathname.startsWith('/hub/') || pathname.startsWith('/hubs/')) {
        return 'hub';
    }
    if (pathname.startsWith('/teams/') || pathname.startsWith('/nfl/teams/')) {
        return 'hub';
    }
    if (pathname.startsWith('/category/sports')) {
        return 'sports';
    }
    if (pathname.startsWith('/category')) {
        return 'category';
    }
    if (pathname.startsWith('/markets')) {
        return 'event';
    }
    if (pathname.startsWith('/events')) {
        return 'category';
    }
    if (pathname.startsWith('/portfolio')) {
        return 'portfolio';
    }
    if (pathname.startsWith('/watchlist')) {
        return 'watchlist';
    }
    if (pathname.startsWith('/ideas')) {
        return 'ideas';
    }
    if (pathname.startsWith('/account')) {
        return 'account';
    }
    if (pathname.startsWith('/calendar')) {
        return 'live';
    }
    if (pathname.startsWith('/sign-up')) {
        return 'signup_step';
    }
    if (pathname.startsWith('/browse')) {
        return 'browse';
    }
    return 'other';
}

export function resolvePageName(
    pathname: string,
    resolveEventTitle?: (eventTicker: string) => string | undefined,
): string {
    const nameSplit = pathname.split('/');
    const firstSegment = nameSplit[1];
    const secondSegment = nameSplit.length > 2 ? nameSplit[2] : '';
    const thirdSegment = nameSplit.length > 3 ? nameSplit[3] : '';
    const fourthSegment = nameSplit.length > 4 ? nameSplit[4] : '';

    if (pathname === '/') {
        return 'Explore';
    }
    if (firstSegment.startsWith('search')) {
        return 'Search';
    }
    if (firstSegment.startsWith('browse')) {
        return 'Browse';
    }
    if (firstSegment.startsWith('markets')) {
        if (fourthSegment) {
            const title = resolveEventTitle?.(fourthSegment.toUpperCase());
            if (title) {
                return title;
            }
            return fourthSegment;
        }
        if (thirdSegment) {
            return decodeURIComponent(thirdSegment.replace(/-/g, ' '));
        }
        return 'Trading';
    }
    if (firstSegment.startsWith('category')) {
        if (secondSegment.startsWith('sports')) {
            if (fourthSegment) {
                return toTitleCase(decodeURIComponent(fourthSegment.replace(/-/g, ' ')));
            }
            if (thirdSegment) {
                return upperFirstLetter(decodeURIComponent(thirdSegment.replace(/-/g, ' ')));
            }
            return 'Sports';
        }
        if (thirdSegment === 'primaries') {
            return 'Primaries';
        }
        return upperFirstLetter(decodeURIComponent(secondSegment.replace(/-/g, ' ')));
    }
    if (firstSegment.startsWith('portfolio')) {
        return 'Portfolio';
    }
    if (firstSegment.startsWith('watchlist')) {
        return 'Watchlist';
    }
    if (firstSegment.startsWith('ideas')) {
        return 'Social';
    }
    if (firstSegment.startsWith('account')) {
        return 'Account';
    }
    if (firstSegment.startsWith('calendar')) {
        return 'Live';
    }
    if (firstSegment.startsWith('sign-up')) {
        switch (secondSegment) {
            case 'verify':
                return 'Email verification';
            case 'birthday':
                return 'Birthday';
            case 'phone-number':
                return 'Enter phone number';
            case 'phone-verify':
                return 'Phone number verification';
            case 'prefill':
                return 'Identity prefill start';
            case 'prefill-verify':
                return 'Identity prefill OTP';
            case 'prefill-review':
                return 'Review prefilled profile';
            case 'profile':
                return 'Enter profile info';
            case 'identity':
                return 'Document verification';
            case 'banking':
                return 'Deposit funds';
            case 'gift':
                return 'Sign Up - Gift';
            case 'survey':
                return 'Attribution survey';
            case 'on-the-go':
                return 'Download the app page';
            case 'review-needed':
                return 'Manual review needed';
            case 'rejected':
                return 'Application rejected';
            case 'two-factor-code':
                return 'Two Factor Code';
            case 'two-factor-code-removed':
                return 'Remove Two Factor Code';
            case 'duplicate-account':
                return 'Duplicate account page';
            case 'reactivate-account':
                return 'Reactivate account';
            default:
                return 'Sign Up';
        }
    }
    if (firstSegment.startsWith('hub')) {
        if (secondSegment) {
            const formattedSlug = toTitleCase(secondSegment.replace(/-/g, ' '));
            return `Hub: ${formattedSlug}`;
        }
        return 'Hub';
    }
    if (firstSegment === 'teams' && thirdSegment) {
        return `Team: ${toTitleCase(decodeURIComponent(thirdSegment.replace(/[-+]/g, ' ')))}`;
    }
    if (firstSegment === 'nfl' && secondSegment === 'teams' && thirdSegment) {
        return `Team: ${toTitleCase(decodeURIComponent(thirdSegment.replace(/[-+]/g, ' ')))}`;
    }
    return 'Other';
}

export function resolvePageId(pathname: string, searchParams: URLSearchParams): string {
    const nameSplit = pathname.split('/');
    const firstSegment = nameSplit[1];
    const secondSegment = nameSplit.length > 2 ? nameSplit[2] : '';
    const thirdSegment = nameSplit.length > 3 ? nameSplit[3] : '';
    const fourthSegment = nameSplit.length > 4 ? nameSplit[4] : '';

    if (pathname === '/') {
        return 'explore';
    }
    if (firstSegment.startsWith('search')) {
        return searchParams.get('search') || 'search';
    }
    if (firstSegment.startsWith('markets')) {
        return fourthSegment ? fourthSegment.toUpperCase() : thirdSegment || 'market';
    }
    if (firstSegment.startsWith('category')) {
        if (secondSegment.startsWith('sports')) {
            return fourthSegment || thirdSegment || 'sports';
        }
        return thirdSegment || secondSegment;
    }
    if (firstSegment.startsWith('browse')) {
        return 'browse';
    }
    if (firstSegment.startsWith('portfolio')) {
        return 'portfolio';
    }
    if (firstSegment.startsWith('watchlist')) {
        return 'watchlist';
    }
    if (firstSegment.startsWith('ideas')) {
        return 'ideas';
    }
    if (firstSegment.startsWith('account')) {
        return 'account';
    }
    if (firstSegment.startsWith('calendar')) {
        return 'live';
    }
    if (firstSegment.startsWith('sign-up')) {
        return secondSegment || 'sign-up';
    }
    if (firstSegment.startsWith('hub')) {
        return secondSegment || 'hub';
    }
    if (firstSegment === 'teams') {
        return thirdSegment || secondSegment || 'team';
    }
    if (firstSegment === 'nfl' && secondSegment === 'teams') {
        return thirdSegment || 'team';
    }
    return 'other';
}
